import { AnyObject } from "../../../global";
import OperatorType from "./enums/OperatorType";
import FieldRenderType, { TextRenderFieldTypeList } from "./enums/FieldRenderType";
import { ShowDecision } from "./types/Decision";
import { SchemaField } from "./types/SchemaField";
import { ConflictSuffix } from "./enums/Conflict";

/**
 * @typedef IsFieldVisibleConfig
 */
interface IsFieldVisibleConfig {
  formValues: AnyObject;
  schemaObj: SchemaField;
  schemaList: Array<SchemaField>;
}

/**
 * @typedef EvaluateDecisionMapType
 */
interface EvaluateDecisionMapType {
  formValues: AnyObject;
  decisionObj: ShowDecision;
  schemaList: Array<SchemaField>;
}

/**
 * @class DecisionEvaluator It contains the methods to evaluate if an field is visible or not.
 */
export class DecisionEvaluator {
  /**
   * Call this method to evaluate if field is visible or not.
   * @return {boolean} Will return Boolean
   * @interface IsFieldVisibleConfig
   */
  static isFieldVisible({ formValues, schemaObj, schemaList }: IsFieldVisibleConfig): boolean {
    const decisionObj = schemaObj.showDecision;
    if (!decisionObj || schemaObj.showMandatory) {
      return true;
    }
    return this.evaluateDecisionMap({ formValues, decisionObj, schemaList });
  }

  /**
   * This in internal method to evaluate decision map
   * @interface EvaluateDecisionMapType
   * @return {boolean} Will return boolean
   */
  private static evaluateDecisionMap({ formValues, decisionObj, schemaList }: EvaluateDecisionMapType): boolean {
    const targetFieldSchemaObj = this.getTargetFieldSchemaObj({
      schemaList,
      key: decisionObj?.key
    });

    /**
     * This is the case where `key` exist in decisionMap and the field of that key doesn't exist in schemaList
     * & `operands` does not exist in decision map. In this scenario we want the field to be visible.
     */
    if (!targetFieldSchemaObj && !decisionObj.operands) {
      return true;
    }

    /**
     * this is the case when field is depended on another field, then check parent field's visibility, if parent field is visible then
     * evaluate this field's visibility otherwise this field should also be hidden
     */
    const isParentVisible =
      targetFieldSchemaObj &&
      DecisionEvaluator.isFieldVisible({
        formValues,
        schemaList,
        schemaObj: targetFieldSchemaObj
      });

    if (!isParentVisible && !decisionObj.operands) {
      return false;
    }

    const fieldValue = this.getFieldValue({ targetFieldSchemaObj, formValues });
    const targetFieldValue = this.getFormattedValue({
      value: fieldValue,
      renderType: targetFieldSchemaObj?.renderType
    });

    const valueInDecision = decisionObj?.value;
    const valueToCompare = this.getFormattedValue({
      value: valueInDecision,
      renderType: targetFieldSchemaObj?.renderType
    });

    if (
      ![OperatorType.AND, OperatorType.OR].includes(decisionObj?.operator) &&
      (targetFieldValue === null || valueToCompare === null)
    ) {
      return false;
    }

    switch (decisionObj?.operator) {
      case OperatorType.EQUAL:
        return targetFieldValue === valueToCompare;
      case OperatorType.NOT_EQUAL:
        return targetFieldValue !== valueToCompare;
      case OperatorType.IN:
        if (!Array.isArray(valueToCompare)) {
          return false;
        }
        if (Array.isArray(targetFieldValue)) {
          return !!targetFieldValue.find(item => valueToCompare.includes(item));
        }
        return valueToCompare.includes(targetFieldValue);
      case OperatorType.LESS_THAN:
        return targetFieldValue < valueToCompare;
      case OperatorType.GREATER_THAN:
        return targetFieldValue > valueToCompare;
      case OperatorType.LESS_THAN_EQUAL_TO:
        return targetFieldValue <= valueToCompare;
      case OperatorType.GREATER_THAN_EQUAL_TO:
        return targetFieldValue >= valueToCompare;
      case OperatorType.AND:
        const operands = decisionObj.operands;
        return !operands.find(item => !this.evaluateDecisionMap({ decisionObj: item, formValues, schemaList }));
      case OperatorType.OR:
        return !!decisionObj.operands.find(item =>
          this.evaluateDecisionMap({ decisionObj: item, formValues, schemaList })
        );
      default:
        return false;
    }
  }

  /**
   * This method returns field value, considering conflicts.
   * In case of conflict if keep existing checkbox is selected then it returns original value of field.
   * So that we can evaluate show decision based on that.
   */
  private static getFieldValue({
    targetFieldSchemaObj,
    formValues
  }: {
    targetFieldSchemaObj?: SchemaField;
    formValues: AnyObject;
  }) {
    if (!targetFieldSchemaObj) {
      return null;
    }
    const isKeepExistingSelected = formValues[`${targetFieldSchemaObj.id}${ConflictSuffix.CHECKBOX}`];

    if (isKeepExistingSelected) {
      return formValues[`${targetFieldSchemaObj.id}${ConflictSuffix.VALUE}`];
    }
    return formValues[targetFieldSchemaObj.id];
  }

  /**
   * This method will calculate what would be the field schema against which we need to evaluate value
   * @return {SchemaField | undefined} It will return undefined in case where field which we want
   * to compare doesn't exist in schema
   */
  private static getTargetFieldSchemaObj({ schemaList, key }: { schemaList: Array<SchemaField>; key: string }) {
    return schemaList.find(item => item.id === key);
  }

  /**
   * @description - formats the value as per render type passed
   * @returns - formatted value or null
   */
  private static getFormattedValue({ value, renderType }: { value?: any; renderType?: FieldRenderType }) {
    if (!renderType || value === null || value === undefined || (typeof value === "string" && value.trim() === ""))
      return null;
    if ([FieldRenderType.INTEGER, FieldRenderType.MONEY].includes(renderType)) {
      return isNaN(parseFloat(value)) ? null : parseFloat(value);
    } else if (TextRenderFieldTypeList.includes(renderType)) {
      return value.trim().toLowerCase();
    }
    return value;
  }
}
